import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { PressableScale } from '@/components/shared/redesign/PressableScale';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { GlassCard } from '../GlassCard';

interface MaintenanceCTACardProps {
  onPress: () => void;
  variant?: 'compact' | 'expanded';
}

export const MaintenanceCTACard: React.FC<MaintenanceCTACardProps> = React.memo(({
  onPress,
  variant = 'compact',
}) => {
  const { colors, semantic, components } = useThemeColors();

  if (variant === 'expanded') {
    return (
      <GlassCard borderRadius={16} padding={24}>
        <View style={styles.expandedLayout}>
          <View style={styles.expandedContent}>
            <View style={styles.iconContainer}>
              <RESIDENTS_SVG_COMPONENTS.HAND_WRENCH width={68} height={68} />
            </View>
            <View style={styles.textContent}>
              <Text style={[styles.expandedTitle, { color: colors.text.primary }]}>Something broken?</Text>
              <Text style={[styles.expandedSubtitle, { color: colors.text.dim }]}>
                Submit a new request with a brief description of the problem,{'\n'}
                and we'll take care of the rest.
              </Text>
            </View>
          </View>
          <View style={styles.buttonContainer}>
            <PressableScale
              hover="button"
              style={({ pressed }) => [
                styles.button,
                { backgroundColor: semantic.interactive.primary },
                pressed ? styles.buttonPressed : null,
              ]}
              onPress={onPress}
              accessibilityRole="button"
            >
              <Text style={[styles.buttonText, { color: components.button.primaryText }]}>New request</Text>
            </PressableScale>
          </View>
        </View>
      </GlassCard>
    );
  }

  return (
    <GlassCard borderRadius={16} padding={20}>
      <View style={styles.compactLayout}>
        <Text style={[styles.compactTitle, { color: colors.text.primary }]}>Something broken?</Text>
        <PressableScale
          hover="button"
          style={({ pressed }) => [
            styles.button,
            { backgroundColor: semantic.interactive.primary },
            pressed ? styles.buttonPressed : null,
          ]}
          onPress={onPress}
          accessibilityRole="button"
        >
          <Text style={[styles.buttonText, { color: components.button.primaryText }]}>New request</Text>
        </PressableScale>
      </View>
    </GlassCard>
  );
});

MaintenanceCTACard.displayName = 'MaintenanceCTACard';

const styles = StyleSheet.create({
  compactLayout: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  compactTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  expandedLayout: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  expandedContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 20,
    flex: 1,
  },
  iconContainer: {
    width: 68,
    height: 68,
  },
  textContent: {
    flex: 1,
    gap: 2,
  },
  expandedTitle: {
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 27,
  },
  expandedSubtitle: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 21,
  },
  buttonContainer: {
    alignItems: 'flex-end',
  },
  button: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    minHeight: 34, // 8 + 18 line + 8: exact at 1x, grows with the font
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
    lineHeight: 18,
  },
  buttonPressed: {
    opacity: 0.85,
  },
});
